/**
 * Firestore Error Handler (GRO-35)
 *
 * Provides:
 * - Retry logic with exponential backoff
 * - Error categorization (retryable vs non-retryable)
 * - User-facing error messages
 * - Graceful degradation when offline
 */

import { useSyncStore } from '../stores/useSyncStore';

// Retry configuration
const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_BASE_DELAY = 1000; // 1 second
const MAX_DELAY = 30000; // 30 seconds

// Firestore error codes that are retryable
const RETRYABLE_ERROR_CODES = [
  'unavailable',       // Service unavailable
  'deadline-exceeded', // Timeout
  'resource-exhausted', // Quota exceeded (may be temporary)
  'aborted',           // Operation aborted
  'internal',          // Internal server error
  'unknown',           // Unknown error (network issues often show as this)
];

// Non-retryable error codes
const NON_RETRYABLE_ERROR_CODES = [
  'permission-denied',   // User doesn't have permission
  'not-found',          // Document doesn't exist
  'already-exists',     // Document already exists
  'invalid-argument',   // Bad input
  'unauthenticated',    // Not logged in
  'failed-precondition', // Operation not allowed in current state
];

/**
 * Categorize Firestore error
 * @param {Error} error - The error to categorize
 * @returns {object} - { isRetryable, userMessage, code }
 */
export const categorizeError = (error) => {
  const code = error.code || 'unknown';
  const isRetryable = RETRYABLE_ERROR_CODES.includes(code) ||
                      !NON_RETRYABLE_ERROR_CODES.includes(code);

  // Map to user-friendly messages
  const userMessages = {
    'unavailable': 'Service temporarily unavailable. Retrying...',
    'deadline-exceeded': 'Request took too long. Retrying...',
    'resource-exhausted': 'Too many requests. Please wait...',
    'permission-denied': 'You don\'t have permission for this action.',
    'unauthenticated': 'Please sign in to continue.',
    'not-found': 'The requested data was not found.',
    'already-exists': 'This item already exists.',
    'invalid-argument': 'Invalid data provided.',
    'failed-precondition': 'Cannot perform this action right now.',
    'aborted': 'Operation was interrupted. Retrying...',
    'internal': 'Something went wrong. Retrying...',
    'unknown': 'Connection issue. Retrying...',
  };

  return {
    isRetryable,
    userMessage: userMessages[code] || 'An error occurred. Please try again.',
    code,
    originalMessage: error.message,
  };
};

/**
 * Calculate exponential backoff delay
 * @param {number} attempt - Current attempt number (0-based)
 * @param {number} baseDelay - Base delay in ms
 * @returns {number} - Delay in ms
 */
export const calculateBackoff = (attempt, baseDelay = DEFAULT_BASE_DELAY) => {
  // Exponential backoff with jitter
  const exponentialDelay = baseDelay * Math.pow(2, attempt);
  const jitter = Math.random() * 0.3 * exponentialDelay; // 0-30% jitter
  return Math.min(exponentialDelay + jitter, MAX_DELAY);
};

/**
 * Sleep for a given duration
 * @param {number} ms - Milliseconds to sleep
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Execute a Firestore operation with retry logic
 *
 * @param {function} operation - Async function to execute
 * @param {object} options - Configuration options
 * @param {number} options.maxRetries - Maximum retry attempts (default: 3)
 * @param {number} options.baseDelay - Base delay for backoff (default: 1000ms)
 * @param {string} options.operationName - Name for logging
 * @param {boolean} options.silent - Don't show user-facing errors
 * @returns {Promise<any>} - Result of the operation
 * @throws {Error} - If all retries fail
 */
export const withRetry = async (operation, options = {}) => {
  const {
    maxRetries = DEFAULT_MAX_RETRIES,
    baseDelay = DEFAULT_BASE_DELAY,
    operationName = 'Firestore operation',
    silent = false,
  } = options;

  const syncStore = useSyncStore.getState();
  let lastError = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Check if offline
      if (!syncStore.isOnline) {
        throw new Error('No internet connection');
      }

      // Execute the operation
      const result = await operation();
      return result;

    } catch (error) {
      lastError = error;
      const { isRetryable, userMessage, code } = categorizeError(error);

      // Log the error
      console.warn(
        `[${operationName}] Attempt ${attempt + 1}/${maxRetries + 1} failed:`,
        code,
        error.message
      );

      // If not retryable or max retries reached, throw
      if (!isRetryable || attempt >= maxRetries) {
        if (!silent) {
          syncStore.failSync(userMessage);
        }
        throw error;
      }

      // Calculate delay and wait
      const delay = calculateBackoff(attempt, baseDelay);
      await sleep(delay);
    }
  }

  // Should never reach here, but just in case
  throw lastError;
};

/**
 * Wrap a Firestore service method with error handling and retry
 *
 * @param {function} method - The service method to wrap
 * @param {string} operationName - Name for logging
 * @param {object} options - Additional options
 * @returns {function} - Wrapped method
 */
export const wrapWithErrorHandler = (method, operationName, options = {}) => {
  return async (...args) => {
    return withRetry(
      () => method(...args),
      { operationName, ...options }
    );
  };
};

/**
 * Execute operation with graceful degradation (returns null on failure)
 *
 * @param {function} operation - Async function to execute
 * @param {any} fallback - Value to return on failure (default: null)
 * @param {string} operationName - Name for logging
 * @returns {Promise<any>} - Result or fallback
 */
export const withGracefulDegradation = async (operation, fallback = null, operationName = 'operation') => {
  try {
    return await withRetry(operation, { operationName, silent: true });
  } catch (error) {
    console.warn(`[${operationName}] Failed, using fallback:`, error.message);
    return fallback;
  }
};

/**
 * Check if Firestore is available
 * @returns {boolean}
 */
export const isFirestoreAvailable = () => {
  const { isOnline, status } = useSyncStore.getState();
  return isOnline && status !== 'error';
};

export default {
  withRetry,
  wrapWithErrorHandler,
  withGracefulDegradation,
  categorizeError,
  calculateBackoff,
  isFirestoreAvailable,
};
